import { apiService } from "@/services/apiService";
import type { AnyClock } from "@repo/lib/clock";
import { ObjectKeyHasher } from "@repo/lib/keyHasher";
import {
  RepoCache,
  type GetObservableOptions,
} from "@repo/shared-client/repoCache";
import type { Municipality, PromotionCode } from "@repo/types";
import { cacheDefaults } from "./common";
import { of, type Observable } from "rxjs";
import { nullObservable } from "@repo/shared-client/commonObservables";
import { baseRepo } from "./baseRepo";

type StripeGetCouponKey = { type: "getCoupon"; code: string };
type StripeGetStatesKey = { type: "getStates" };
type StripeGetMunicipalities = { type: "getMunicipalities"; state: string };
type StripeKey =
  | StripeGetCouponKey
  | StripeGetStatesKey
  | StripeGetMunicipalities;

export type StripeCouponDto = PromotionCode;
export type StripeStatesDto = string[];
export type StripeMunicipalitiesDto = Municipality[];

type StripeData = StripeCouponDto | StripeStatesDto | StripeMunicipalitiesDto;

export class StripeRepository {
  private cache: RepoCache<string, StripeData, ObjectKeyHasher, AnyClock>;
  private keyHasher: ObjectKeyHasher;

  /** Load and depaginate municipalities from server. */
  private async internalGetMunicipalities(
    state: string
  ): Promise<Municipality[]> {
    if (!state) return [];

    const limit = 100;
    let page = 1;
    let remainingPages = true;

    const municipalities: Municipality[] = [];
    while (remainingPages) {
      const { data, pagination } = (
        await apiService.v1.getMunicipalitiesForState(state, {
          page,
          limit,
        })
      ).data;
      if (pagination.nextPage === null) {
        remainingPages = false;
      } else {
        page++;
      }
      for (const municipality of data) {
        municipalities.push(municipality);
      }
    }
    return municipalities;
  }

  constructor() {
    this.keyHasher = new ObjectKeyHasher({ sortKeys: false });
    this.cache = new RepoCache({
      requestKeyHasher: this.keyHasher,
      expiresAfterSeconds: cacheDefaults.expiresAfterSeconds,
      purgeExpirationAfterSeconds: cacheDefaults.purgeExpiredAfterSeconds,
      fetch: async ({ requestKey, publish }) => {
        const key = requestKey as StripeKey;

        switch (key.type) {
          case "getCoupon": {
            const { code } = key;
            const coupon = (await apiService.v1.getCoupon(code)).data;
            publish(this.keyHasher.hash(key), coupon);
            break;
          }
          case "getStates": {
            const states = (await apiService.v1.getStates()).data.data;
            publish(this.keyHasher.hash(key), states);
            break;
          }
          case "getMunicipalities": {
            const { state } = key;
            const municipalities = await this.internalGetMunicipalities(state);
            publish(this.keyHasher.hash(key), municipalities);
            break;
          }
        }
      },
      collect: ({ requestKey, table }) => {
        const strKey = this.keyHasher.hash(requestKey);
        return table.get(strKey);
      },
      affectedKeys: ({ entryKey, affectsKey }) => {
        // extract
        const key = this.keyHasher.unhash(entryKey);
        // affect
        affectsKey(key);
      },
    });

    baseRepo.addRepo(this);
  }

  private getCouponKey(code: string): StripeGetCouponKey {
    return { type: "getCoupon", code };
  }
  private getStatesKey(): StripeGetStatesKey {
    return { type: "getStates" };
  }
  private getMunicipalitiesKey(state: string): StripeGetMunicipalities {
    return { type: "getMunicipalities", state };
  }

  getCoupon(
    code: string,
    options?: GetObservableOptions
  ): Observable<StripeCouponDto | null> {
    if (!code) {
      return nullObservable;
    }
    const key = this.getCouponKey(code);
    return this.cache.observable(key, options);
  }
  getCouponCached(code: string): StripeCouponDto | null {
    const key = this.getCouponKey(code);
    return this.cache.collectIfUnexpired(key, null);
  }

  getStates(options?: GetObservableOptions): Observable<StripeStatesDto> {
    const key = this.getStatesKey();
    return this.cache.observable(key, options);
  }
  getStatesCached(): StripeStatesDto | null {
    const key = this.getStatesKey();
    return this.cache.collectIfUnexpired(key, null);
  }

  getMunicipalities(
    state: string,
    options?: GetObservableOptions
  ): Observable<StripeMunicipalitiesDto> {
    if (!state) {
      return of([]);
    }
    const key = this.getMunicipalitiesKey(state);
    return this.cache.observable(key, options);
  }
  getMunicipalitiesCached(state: string): StripeMunicipalitiesDto | null {
    const key = this.getMunicipalitiesKey(state);
    return this.cache.collectIfUnexpired(key, null);
  }

  invalidateCoupon(code: string, refetch: boolean = false) {
    const key = this.getCouponKey(code);
    const strKey = this.keyHasher.hash(key);

    refetch = refetch && this.cache.table.has(strKey);

    this.cache.invalidateEntry(strKey);

    if (refetch) this.cache.fetch(key);
  }

  invalidateStates(refetch: boolean = false) {
    const key = this.getStatesKey();
    const strKey = this.keyHasher.hash(key);

    refetch = refetch && this.cache.table.has(strKey);

    this.cache.invalidateEntry(strKey);

    if (refetch) this.cache.fetch(key);
  }

  invalidateMunicipalities(state: string, refetch: boolean = false) {
    const key = this.getMunicipalitiesKey(state);
    const strKey = this.keyHasher.hash(key);

    refetch = refetch && this.cache.table.has(strKey);

    this.cache.invalidateEntry(strKey);

    if (refetch) this.cache.fetch(key);
  }

  invalidateAll() {
    this.cache.clear();
  }
}

export const stripeRepo = new StripeRepository();
